import React from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ICCamera, ICImage } from '../../../../icons';

type FileUploadProps = {
    setOpenCamera: React.Dispatch<React.SetStateAction<boolean>>;
    onSelectedImage: (image: File) => void;
    /** Stack camera above gallery picker (used on Add a Car) */
    stacked?: boolean;
};

export const FileUpload: React.FC<FileUploadProps> = ({
    setOpenCamera,
    onSelectedImage,
    stacked = false,
}) => {
    const { showError } = useNotification();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [fileStatusText, setFileStatusText] =
        React.useState('No file selected');

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files || [];
        if (selectedFiles.length === 0) {
            setFileStatusText('No file selected');
            return;
        }
        if (selectedFiles.length === 1) {
            setFileStatusText(selectedFiles[0].name);
        } else {
            setFileStatusText(`${selectedFiles.length} files selected`);
        }
        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            if (file.size > 5 * 1024 * 1024) {
                showError('Error', 'File size should be less than 5MB');
                continue;
            }
            onSelectedImage(file);
        }
        event.target.value = '';
        setFileStatusText('No file selected');
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const cardClass =
        'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-5 cursor-pointer transition-colors w-full';

    const cameraButton = (
        <button
            type="button"
            onClick={() => setOpenCamera(true)}
            className={`${cardClass} border-[#3FB483] bg-[#EDFAF4] hover:bg-[#D8F5E8]`}
        >
            <ICCamera fill="#3FB483" width={28} height={28} />
            <span className="font-semibold text-high-em text-sm">Camera</span>
            <span className="text-xs text-med-em text-center">
                Take a photo
            </span>
        </button>
    );

    const galleryButton = (
        <button
            type="button"
            onClick={triggerFileInput}
            className={`${cardClass} border-[#7357FF] bg-[var(--tc-primary-p-50)] hover:bg-[var(--tc-primary-p-75)]`}
        >
            <ICImage fill="#7357FF" width={28} height={28} />
            <span className="font-semibold text-high-em text-sm">
                Select Image
            </span>
            <span className="text-xs text-med-em text-center">
                Choose from gallery
            </span>
        </button>
    );

    return (
        <div className="flex flex-col gap-3 w-full">
            <div
                className={
                    stacked
                        ? 'flex flex-col gap-3 w-full'
                        : 'grid grid-cols-2 gap-3 w-full'
                }
            >
                {stacked ? (
                    <>
                        {cameraButton}
                        {galleryButton}
                    </>
                ) : (
                    <>
                        {galleryButton}
                        {cameraButton}
                    </>
                )}
            </div>
            <label className="at-file-upload-row flex w-full items-center gap-2 rounded-lg border border-[var(--tc-outlines-med)] bg-white px-3 py-2 min-h-[42px] cursor-pointer">
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="at-file-upload-input"
                />
                <span
                    className="at-file-upload-status flex-1 min-w-0 text-sm text-med-em truncate"
                    title={fileStatusText}
                >
                    {fileStatusText}
                </span>
            </label>
        </div>
    );
};
